import React, { useMemo } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Area,
  AreaChart,
  Legend,
} from 'recharts';
import {
  TrendingUp,
  TrendingDown,
  Package,
  ShoppingCart,
  DollarSign,
  Users,
  Calendar,
  ArrowUpRight,
  ArrowDownRight,
  Activity,
  BarChart3,
  PieChart as PieChartIcon,
} from 'lucide-react';

interface AnalyticsData {
  consumptionTrends: Array<{
    date: string;
    received: number;
    issued: number;
    adjustments: number;
  }>;
  categoryBreakdown: Array<{
    name: string;
    value: number;
    color: string;
  }>;
  vendorPerformance: Array<{
    vendor_name: string;
    total_spent: number;
    on_time_delivery: number;
    quality_score: number;
    po_count: number;
  }>;
  stockMovement: Array<{
    item: string;
    incoming: number;
    outgoing: number;
  }>;
  expiryAnalysis: Array<{
    category: string;
    expiring_30_days: number;
    expiring_7_days: number;
    expired: number;
  }>;
  departmentUsage: Array<{
    department: string;
    requisitions: number;
    amount: number;
  }>;
  leadTimeAnalysis: Array<{
    vendor: string;
    avg_lead_time: number;
    on_time_rate: number;
  }>;
}

interface AdvancedAnalyticsPageProps {
  data?: AnalyticsData;
}

const COLORS = ['#2563eb', '#16a34a', '#dc2626', '#d97706', '#7c3aed', '#0891b2', '#c026d3', '#65a30d'];

export default function AdvancedAnalyticsPage({ data }: AdvancedAnalyticsPageProps) {
  const [timeRange, setTimeRange] = React.useState('30');
  const [selectedCategory, setSelectedCategory] = React.useState('all');

  // Mock data for demonstration
  const analyticsData: AnalyticsData = data || useMemo(() => ({
    consumptionTrends: [
      { date: '2026-01', received: 1200, issued: 800, adjustments: 50 },
      { date: '2026-02', received: 1500, issued: 900, adjustments: 30 },
      { date: '2026-03', received: 1100, issued: 1000, adjustments: 20 },
      { date: '2026-04', received: 1800, issued: 1200, adjustments: 40 },
      { date: '2026-05', received: 2000, issued: 1100, adjustments: 60 },
      { date: '2026-06', received: 1600, issued: 1300, adjustments: 25 },
    ],
    categoryBreakdown: [
      { name: 'Pharmaceuticals', value: 450000, color: '#2563eb' },
      { name: 'Surgical Supplies', value: 320000, color: '#16a34a' },
      { name: 'Laboratory', value: 180000, color: '#dc2626' },
      { name: 'Medical Equipment', value: 150000, color: '#d97706' },
      { name: 'Consumables', value: 120000, color: '#7c3aed' },
    ],
    vendorPerformance: [
      { vendor_name: 'MedSupply Co.', total_spent: 520000, on_time_delivery: 95, quality_score: 92, po_count: 45 },
      { vendor_name: 'Pharma Distributors', total_spent: 380000, on_time_delivery: 88, quality_score: 96, po_count: 32 },
      { vendor_name: 'Lab Equipment Inc.', total_spent: 210000, on_time_delivery: 92, quality_score: 89, po_count: 18 },
      { vendor_name: 'Surgical Tools Ltd.', total_spent: 175000, on_time_delivery: 85, quality_score: 91, po_count: 24 },
      { vendor_name: 'Medical Devices Corp.', total_spent: 145000, on_time_delivery: 78, quality_score: 94, po_count: 12 },
    ],
    stockMovement: [
      { item: 'Surgical Gloves', incoming: 5000, outgoing: 4500 },
      { item: 'Syringes', incoming: 3000, outgoing: 2800 },
      { item: 'Bandages', incoming: 2000, outgoing: 1900 },
      { item: 'Medications', incoming: 1500, outgoing: 1400 },
      { item: 'IV Sets', incoming: 800, outgoing: 750 },
    ],
    expiryAnalysis: [
      { category: 'Pharmaceuticals', expiring_30_days: 45, expiring_7_days: 12, expired: 3 },
      { category: 'Surgical Supplies', expiring_30_days: 20, expiring_7_days: 5, expired: 1 },
      { category: 'Laboratory', expiring_30_days: 30, expiring_7_days: 8, expired: 2 },
      { category: 'Consumables', expiring_30_days: 15, expiring_7_days: 3, expired: 0 },
    ],
    departmentUsage: [
      { department: 'Emergency', requisitions: 156, amount: 250000 },
      { department: 'Pharmacy', requisitions: 142, amount: 320000 },
      { department: 'Surgery', requisitions: 98, amount: 180000 },
      { department: 'ICU', requisitions: 87, amount: 210000 },
      { department: 'Laboratory', requisitions: 75, amount: 95000 },
      { department: 'Radiology', requisitions: 45, amount: 65000 },
    ],
    leadTimeAnalysis: [
      { vendor: 'MedSupply Co.', avg_lead_time: 3.2, on_time_rate: 95 },
      { vendor: 'Pharma Distributors', avg_lead_time: 2.8, on_time_rate: 88 },
      { vendor: 'Lab Equipment Inc.', avg_lead_time: 5.1, on_time_rate: 92 },
      { vendor: 'Surgical Tools Ltd.', avg_lead_time: 4.5, on_time_rate: 85 },
      { vendor: 'Medical Devices Corp.', avg_lead_time: 6.2, on_time_rate: 78 },
    ],
  }), []);

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      maximumFractionDigits: 0,
    }).format(value);
  };

  const totalValue = useMemo(() => {
    return analyticsData.categoryBreakdown.reduce((sum, cat) => sum + cat.value, 0);
  }, [analyticsData]);

  const totalRequisitions = useMemo(() => {
    return analyticsData.departmentUsage.reduce((sum, dept) => sum + dept.requisitions, 0);
  }, [analyticsData]);

  const avgLeadTime = useMemo(() => {
    const sum = analyticsData.leadTimeAnalysis.reduce((s, v) => s + v.avg_lead_time, 0);
    return (sum / analyticsData.leadTimeAnalysis.length).toFixed(1);
  }, [analyticsData]);

  const onTimeRate = useMemo(() => {
    const sum = analyticsData.leadTimeAnalysis.reduce((s, v) => s + v.on_time_rate, 0);
    return (sum / analyticsData.leadTimeAnalysis.length).toFixed(0);
  }, [analyticsData]);

  return (
    <div className="container mx-auto py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Advanced Analytics</h1>
          <p className="text-muted-foreground">
            Comprehensive insights into inventory performance and trends
          </p>
        </div>
        <div className="flex gap-2">
          <Select value={timeRange} onValueChange={setTimeRange}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Time range" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
              <SelectItem value="365">Last year</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline">
            Export Report
          </Button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total Inventory Value</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatCurrency(totalValue)}</div>
            <div className="flex items-center text-xs text-green-600">
              <ArrowUpRight className="h-3 w-3" />
              +12.5% from last period
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Total Requisitions</CardTitle>
            <ShoppingCart className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalRequisitions}</div>
            <div className="flex items-center text-xs text-green-600">
              <ArrowUpRight className="h-3 w-3" />
              +8.3% from last period
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">Avg. Lead Time</CardTitle>
            <Calendar className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{avgLeadTime} days</div>
            <div className="flex items-center text-xs text-red-600">
              <ArrowDownRight className="h-3 w-3" />
              +0.5 days from last period
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium">On-Time Delivery</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{onTimeRate}%</div>
            <div className="flex items-center text-xs text-green-600">
              <ArrowUpRight className="h-3 w-3" />
              +2.1% from last period
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <Tabs defaultValue="overview" className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="consumption">Consumption</TabsTrigger>
          <TabsTrigger value="vendor">Vendor</TabsTrigger>
          <TabsTrigger value="expiry">Expiry</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            {/* Category Distribution */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Inventory by Category</CardTitle>
                <CardDescription>Value distribution across categories</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={analyticsData.categoryBreakdown}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="value"
                        label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      >
                        {analyticsData.categoryBreakdown.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value: number) => formatCurrency(value)} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Department Usage */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Requisitions by Department</CardTitle>
                <CardDescription>Volume and spending by department</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={analyticsData.departmentUsage} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis type="number" />
                      <YAxis type="category" dataKey="department" width={80} tick={{ fontSize: 12 }} />
                      <Tooltip formatter={(value: number) => formatCurrency(value)} />
                      <Bar dataKey="amount" fill="#2563eb" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Consumption Trends */}
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle className="text-base">Stock Movement Trends</CardTitle>
                <CardDescription>Incoming vs outgoing inventory over time</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={analyticsData.consumptionTrends}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Area
                        type="monotone"
                        dataKey="received"
                        stackId="1"
                        stroke="#16a34a"
                        fill="#16a34a"
                        fillOpacity={0.6}
                        name="Received"
                      />
                      <Area
                        type="monotone"
                        dataKey="issued"
                        stackId="2"
                        stroke="#dc2626"
                        fill="#dc2626"
                        fillOpacity={0.6}
                        name="Issued"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="consumption" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            {/* Stock Movement */}
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle className="text-base">Stock Movement by Item</CardTitle>
                <CardDescription>Top moving items - incoming vs outgoing</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={analyticsData.stockMovement}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="item" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="incoming" fill="#16a34a" name="Incoming" />
                      <Bar dataKey="outgoing" fill="#dc2626" name="Outgoing" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Daily Consumption Line */}
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle className="text-base">Consumption Over Time</CardTitle>
                <CardDescription>Daily issued items trend</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={analyticsData.consumptionTrends}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="date" />
                      <YAxis />
                      <Tooltip />
                      <Line
                        type="monotone"
                        dataKey="issued"
                        stroke="#dc2626"
                        strokeWidth={2}
                        dot={{ fill: '#dc2626' }}
                        name="Issued"
                      />
                      <Line
                        type="monotone"
                        dataKey="adjustments"
                        stroke="#d97706"
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        dot={{ fill: '#d97706' }}
                        name="Adjustments"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="vendor" className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            {/* Vendor Performance */}
            <Card className="md:col-span-2">
              <CardHeader>
                <CardTitle className="text-base">Vendor Performance</CardTitle>
                <CardDescription>On-time delivery and quality scores</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={analyticsData.vendorPerformance}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="vendor_name" tick={{ fontSize: 11 }} />
                      <YAxis yAxisId="left" />
                      <YAxis yAxisId="right" orientation="right" domain={[0, 100]} />
                      <Tooltip />
                      <Legend />
                      <Bar
                        yAxisId="left"
                        dataKey="on_time_delivery"
                        fill="#16a34a"
                        name="On-Time %"
                        radius={[4, 4, 0, 0]}
                      />
                      <Bar
                        yAxisId="right"
                        dataKey="quality_score"
                        fill="#2563eb"
                        name="Quality Score"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Lead Time Analysis */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Lead Time Analysis</CardTitle>
                <CardDescription>Average delivery time by vendor</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={analyticsData.leadTimeAnalysis}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="vendor" tick={{ fontSize: 10 }} />
                      <YAxis />
                      <Tooltip />
                      <Bar dataKey="avg_lead_time" fill="#d97706" name="Avg. Days" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            {/* Spending by Vendor */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Spending by Vendor</CardTitle>
                <CardDescription>Total spend distribution</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={analyticsData.vendorPerformance}
                        cx="50%"
                        cy="50%"
                        outerRadius={100}
                        dataKey="total_spent"
                        nameKey="vendor_name"
                        label={({ vendor_name, percent }) =>
                          `${vendor_name} ${(percent * 100).toFixed(0)}%`
                        }
                      >
                        {analyticsData.vendorPerformance.map((_, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip formatter={(value: number) => formatCurrency(value)} />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="expiry" className="space-y-4">
          <Card className="md:col-span-2">
            <CardHeader>
              <CardTitle className="text-base">Expiry Analysis by Category</CardTitle>
              <CardDescription>Items approaching expiration</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={analyticsData.expiryAnalysis}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="category" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="expiring_30_days" fill="#d97706" name="Expiring 30 days" />
                    <Bar dataKey="expiring_7_days" fill="#dc2626" name="Expiring 7 days" />
                    <Bar dataKey="expired" fill="#991b1b" name="Expired" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Expiry Stats */}
          <div className="grid gap-4 md:grid-cols-3">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-warning">Expiring Soon</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {analyticsData.expiryAnalysis.reduce((sum, c) => sum + c.expiring_30_days, 0)}
                </div>
                <p className="text-xs text-muted-foreground">Items expiring in 30 days</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-error">Critical</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {analyticsData.expiryAnalysis.reduce((sum, c) => sum + c.expiring_7_days, 0)}
                </div>
                <p className="text-xs text-muted-foreground">Items expiring in 7 days</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium">Expired</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">
                  {analyticsData.expiryAnalysis.reduce((sum, c) => sum + c.expired, 0)}
                </div>
                <p className="text-xs text-muted-foreground">Items already expired</p>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}
